// ==========================
// Compliance Training Page
// ==========================

import './styles.scss';
import React, { ChangeEvent, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Spinner } from 'react-bootstrap';
import { Typography } from 'Components/atoms';
import {
    DEFAULT_ERROR_MSG,
    ERROR_VARIANT,
    ERROR_MESSAGES,
    COMPLIANCE_TRAINING_MODULE_CONTENT,
} from 'Core-utils/constants/constants';
import { ToggleTextPanel } from 'Components/molecules';
import { LaunchTrainingModal } from 'Components/organisms/LaunchTrainingModal';
import { ToastsContext } from 'Rdx/contexts/ToastsContext';
import { useI18n } from 'Hooks/useI18n';
import { useTenantSettings } from 'Hooks/useTenantSettings';
import {
    parseComplianceTrainingModulesFromI18n,
    mergeComplianceTrainingModules,
    getComplianceTrainingCustomModulesSettingSignature,
    parseComplianceTrainingCustomModulesFromSettingSignature,
} from 'Core-utils/i18n/complianceTrainingModuleI18n';
import {
    AmplifierConnectorReadinessOperation,
    createTrainingCampaign,
    ensureAmplifierConnectorReadiness,
    getInstalledConnectors,
    getProvidersData,
} from 'Apis/library';
import {
    ScheduleSidePanel,
    TrainingScheduleRecord,
    ModuleTrainingData,
} from 'Components/organisms/ComplianceTrainingScheduleModal/ScheduleSidePanel';
import {
    AMP_SETTINGS,
    ComplianceTrainingSettingSchema,
    ConnectorUpsertDto,
    KIND,
    ProviderDto,
} from '@ampsec/platform-client';
import { useApiData } from 'Hooks/useApiData';
import logger from 'Core-utils/logger';
import {
    findAmplifierProviderId,
    isApiResponseSuccess,
    extractErrorMessage,
    toggleModuleEnabled,
    getEnabledSubjectIds,
    setModuleLaunchingState,
    updateModuleOverdueDays,
    buildComplianceTrainingModulesState,
    setModuleLaunchCooldown,
    setLaunchCooldownExpiresAt,
    clearLaunchCooldownExpiresAt,
    parseLaunchNowCooldownMs,
} from './utils/helpers';
import { TrainingModuleCard } from './utils/TrainingModuleCard';
import { LaunchConfirmationModalState, TrainingCampaignPayload, TrainingModuleState } from './utils/types';
import { TrainingAndCoachingTabs } from 'Components/organisms/TrainingAndCoachingTabs';
// ============================================================================
// Constants
// ============================================================================

const DEFAULT_OVERDUE_DAYS = '30';
const API_LIMIT = 250;
const DEFAULT_MODAL_STATE: LaunchConfirmationModalState = {
    isOpen: false,
    subject: '',
    name: '',
};

// ============================================================================
// Main Component
// ============================================================================

export const ComplianceTrainingPage = () => {
    // ========================================================================
    // Hooks
    // ========================================================================

    const { t, loading: i18nLoading } = useI18n();
    const { addToast } = useContext(ToastsContext);
    const {
        tenantSettings,
        settingsLoading,
        getAmpSettingValue,
        updateAmpSetting,
        createAmpSetting,
        refreshTenantSettings,
    } = useTenantSettings();

    // ========================================================================
    // API Data
    // ========================================================================

    const { data: installedConnectors, refresh: refreshConnectors } = useApiData<
        ConnectorUpsertDto,
        ConnectorUpsertDto
    >(
        KIND.CONNECTORS,
        () => getInstalledConnectors({ limit: API_LIMIT }),
        (a) => a,
    );

    const { data: providersList } = useApiData<ProviderDto, ProviderDto>(
        KIND.PROVIDERS,
        () => getProvidersData({ limit: API_LIMIT }),
        (a) => a,
    );

    // ========================================================================
    // State
    // ========================================================================

    const [modulesState, setModulesState] = useState<TrainingModuleState[]>([]);
    const [isSidePanelOpen, setSidePanelOpen] = useState(false);
    const [activeModuleForSchedule, setActiveModuleForSchedule] = useState<{
        id: string;
        name: string;
        subject: string;
        moduleData: ModuleTrainingData;
    } | null>(null);
    const [isSavingSchedules, setIsSavingSchedules] = useState(false);
    const [launchConfirmationModal, setLaunchConfirmationModal] =
        useState<LaunchConfirmationModalState>(DEFAULT_MODAL_STATE);
    const [savingComplianceTrainingEnabled, setSavingComplianceTrainingEnabled] = useState(false);
    const launchCooldownTimersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

    const launchNowCooldownMs = useMemo(
        () => parseLaunchNowCooldownMs(t('COMPLIANCE_TRAINING_LAUNCH_NOW_DEFAULT_COOLDOWN_MS')),
        [t],
    );

    const complianceTrainingModulesRaw = t(COMPLIANCE_TRAINING_MODULE_CONTENT, '[]');
    const contentfulTrainingSubjects = useMemo(
        () => parseComplianceTrainingModulesFromI18n(complianceTrainingModulesRaw),
        [complianceTrainingModulesRaw],
    );

    const customModulesSettingSignature = useMemo(
        () =>
            getComplianceTrainingCustomModulesSettingSignature(
                tenantSettings as Record<string, { value?: unknown } | undefined>,
                AMP_SETTINGS?.tenant?.COMPLIANCE_TRAINING_CUSTOM_MODULES?.id,
            ),
        [tenantSettings],
    );

    const complianceTrainingSettingsSignature = useMemo(() => {
        const settingKey = AMP_SETTINGS?.tenant?.COMPLIANCE_TRAINING_SETTINGS?.id;
        if (!settingKey) {
            return '';
        }
        const raw = (tenantSettings as Record<string, { value?: unknown } | undefined>)?.[settingKey]?.value;
        if (raw === undefined || raw === null) {
            return '';
        }
        return typeof raw === 'string' ? raw : JSON.stringify(raw);
    }, [tenantSettings]);

    // ========================================================================
    // Computed Values (Memoized)
    // ========================================================================

    const amplifierProviderId = useMemo(
        () => findAmplifierProviderId(Array.isArray(providersList) ? providersList : undefined),
        [providersList],
    );

    const amplifierSyncIntervalSeconds = useMemo(() => {
        if (!amplifierProviderId || !Array.isArray(installedConnectors)) {
            return undefined;
        }
        const connector = installedConnectors.find((c) => c.pid === amplifierProviderId);
        return connector?.syncIntervalSeconds;
    }, [amplifierProviderId, installedConnectors]);

    const allScheduleRecords: TrainingScheduleRecord[] = useMemo(() => {
        if (!complianceTrainingSettingsSignature) {
            return [];
        }
        try {
            const parsed = JSON.parse(complianceTrainingSettingsSignature);
            const validated = ComplianceTrainingSettingSchema.safeParse(parsed);

            if (validated.success) {
                return validated.data.trainings;
            } else {
                logger.warn('Invalid compliance settings format detected by Zod:', validated.error.format());
                return Array.isArray(parsed?.trainings) ? parsed.trainings : [];
            }
        } catch (err) {
            logger.warn('Error parsing compliance.training.settings:', err);
            return [];
        }
    }, [complianceTrainingSettingsSignature]);

    const complianceTrainingEnabledValue = useMemo(() => {
        try {
            const value = getAmpSettingValue(AMP_SETTINGS?.tenant?.COMPLIANCE_TRAINING_ENABLED?.id);
            return value ?? AMP_SETTINGS?.tenant?.COMPLIANCE_TRAINING_ENABLED?.defaultValue ?? false;
        } catch (err) {
            logger.warn('Error getting COMPLIANCE_TRAINING_ENABLED:', err);
            return false;
        }
    }, [getAmpSettingValue]);

    // ========================================================================
    // Event Handlers
    // ========================================================================

    const handleToggleComplianceTrainingEnabled = useCallback(
        async (ev: ChangeEvent<HTMLInputElement>) => {
            try {
                const settingKey = AMP_SETTINGS?.tenant?.COMPLIANCE_TRAINING_ENABLED?.id;
                if (!settingKey) {
                    addToast({ severity: ERROR_VARIANT, body: ERROR_MESSAGES.SETTINGS_INIT_FAILED });
                    return;
                }

                const newValue = ev.target.checked;
                const existingSetting = Object.keys(tenantSettings as any).find((setting) => setting === settingKey);

                setSavingComplianceTrainingEnabled(true);
                try {
                    if (newValue) {
                        try {
                            await ensureAmplifierConnectorReadiness(
                                AmplifierConnectorReadinessOperation.AMPLIFIER_COMPLIANCE,
                            );
                            await refreshConnectors();
                        } catch (err: any) {
                            addToast({
                                severity: ERROR_VARIANT,
                                body: extractErrorMessage(err, ERROR_MESSAGES.CONNECTOR_SETUP),
                            });
                            return;
                        }
                    }
                    if (existingSetting !== undefined) {
                        await updateAmpSetting(settingKey, newValue);
                    } else {
                        await createAmpSetting(settingKey, newValue);
                    }
                    await refreshTenantSettings();
                } catch (err: any) {
                    addToast({ severity: ERROR_VARIANT, body: extractErrorMessage(err, DEFAULT_ERROR_MSG) });
                } finally {
                    setSavingComplianceTrainingEnabled(false);
                }
            } catch (err: any) {
                logger.error('Error in handleToggleComplianceTrainingEnabled:', err);
                setSavingComplianceTrainingEnabled(false);
            }
        },
        [tenantSettings, updateAmpSetting, createAmpSetting, refreshTenantSettings, addToast, refreshConnectors],
    );

    const handleToggleModule = useCallback(
        async (subjectId: string) => {
            setModulesState((prev) => toggleModuleEnabled(prev, subjectId));
            try {
                const updatedModules = toggleModuleEnabled(modulesState, subjectId);
                const enabledSubjects = getEnabledSubjectIds(updatedModules);
                const settingKey = AMP_SETTINGS.tenant.COMPLIANCE_TRAINING_ACTIVE_SUBJECTS.id;
                const existingSetting = Object.keys(tenantSettings as any).find((setting) => setting === settingKey);

                if (existingSetting !== undefined) {
                    await updateAmpSetting(settingKey, enabledSubjects);
                } else {
                    await createAmpSetting(settingKey, enabledSubjects);
                }
                await refreshTenantSettings();
            } catch (err: any) {
                addToast({ severity: ERROR_VARIANT, body: extractErrorMessage(err, DEFAULT_ERROR_MSG) });
            }
        },
        [modulesState, tenantSettings, updateAmpSetting, createAmpSetting, refreshTenantSettings, addToast],
    );

    const handleOverdueChange = useCallback((subjectId: string, days: string) => {
        setModulesState((prev) => updateModuleOverdueDays(prev, subjectId, days));
    }, []);

    const clearModuleLaunchCooldown = useCallback((subjectId: string) => {
        setModulesState((prev) => setModuleLaunchCooldown(prev, subjectId, null));
        clearLaunchCooldownExpiresAt(subjectId);
        launchCooldownTimersRef.current.delete(subjectId);
    }, []);

    const scheduleLaunchCooldownClear = useCallback(
        (subjectId: string, expiresAt: number) => {
            const existingTimer = launchCooldownTimersRef.current.get(subjectId);
            if (existingTimer) {
                clearTimeout(existingTimer);
            }

            const remainingMs = expiresAt - Date.now();
            if (remainingMs <= 0) {
                clearModuleLaunchCooldown(subjectId);
                return;
            }

            const timer = setTimeout(() => {
                clearModuleLaunchCooldown(subjectId);
            }, remainingMs);

            launchCooldownTimersRef.current.set(subjectId, timer);
        },
        [clearModuleLaunchCooldown],
    );

    const scheduleActiveLaunchCooldowns = useCallback(
        (modules: TrainingModuleState[]) => {
            modules.forEach((module) => {
                if (typeof module.launchCooldownExpiresAt === 'number' && module.launchCooldownExpiresAt > Date.now()) {
                    scheduleLaunchCooldownClear(module.subject, module.launchCooldownExpiresAt);
                }
            });
        },
        [scheduleLaunchCooldownClear],
    );

    const scheduleActiveLaunchCooldownsRef = useRef(scheduleActiveLaunchCooldowns);
    useEffect(() => {
        scheduleActiveLaunchCooldownsRef.current = scheduleActiveLaunchCooldowns;
    }, [scheduleActiveLaunchCooldowns]);

    const handleOpenSidePanel = useCallback(
        (moduleId: string, moduleName: string, moduleSubject: string, moduleData: ModuleTrainingData) => {
            setActiveModuleForSchedule({ id: moduleId, name: moduleName, subject: moduleSubject, moduleData });
            setSidePanelOpen(true);
        },
        [],
    );

    const handleSaveSidePanel = useCallback(
        async (updatedRecords: TrainingScheduleRecord[]) => {
            setIsSavingSchedules(true);
            try {
                const newValue = { trainings: updatedRecords };
                await updateAmpSetting(AMP_SETTINGS.tenant.COMPLIANCE_TRAINING_SETTINGS.id, newValue, true);
                await refreshTenantSettings();
                setSidePanelOpen(false);
                addToast({ severity: 'success', body: ERROR_MESSAGES.SCHEDULES_SAVED });
            } catch (err: any) {
                addToast({ severity: ERROR_VARIANT, body: extractErrorMessage(err, 'Failed to save schedules.') });
            } finally {
                setIsSavingSchedules(false);
            }
        },
        [updateAmpSetting, refreshTenantSettings, addToast],
    );

    const handleConfirmLaunchTraining = useCallback(
        async (dueInDays: number, cohorts: TrainingCampaignPayload['cohorts']) => {
            const { subject: subjectId, name: subjectName } = launchConfirmationModal;
            setLaunchConfirmationModal(DEFAULT_MODAL_STATE);
            setModulesState((prev) => setModuleLaunchingState(prev, subjectId, true));

            const module = modulesState.find((m) => m.subject === subjectId);

            if (!module?.videoUrl || !module?.name) {
                addToast({ severity: 'error', body: ERROR_MESSAGES.TRAINING_LAUNCH_MISSING_DATA });
                setModulesState((prev) => setModuleLaunchingState(prev, subjectId, false));
                return;
            }

            try {
                const payload: TrainingCampaignPayload = {
                    videoUrl: module.videoUrl,
                    subject: module.name,
                    description: module.description,
                    dueInDays,
                    ...(module.thumbnail ? { thumbnailUrl: module.thumbnail } : {}),
                    ...(cohorts && cohorts.length > 0 ? { cohorts } : {}),
                };

                const res = await createTrainingCampaign(payload);

                if (isApiResponseSuccess(res)) {
                    const expiresAt = Date.now() + launchNowCooldownMs;
                    setLaunchCooldownExpiresAt(subjectId, expiresAt);
                    setModulesState((prev) => {
                        const updated = setModuleLaunchingState(prev, subjectId, false);
                        return setModuleLaunchCooldown(updated, subjectId, expiresAt);
                    });
                    scheduleLaunchCooldownClear(subjectId, expiresAt);
                    addToast({
                        severity: 'success',
                        body: ERROR_MESSAGES.TRAINING_LAUNCHED.replace('{subject}', subjectName),
                    });
                    return;
                }

                setModulesState((prev) => setModuleLaunchingState(prev, subjectId, false));
                addToast({ severity: 'error', body: extractErrorMessage(res, ERROR_MESSAGES.TRAINING_LAUNCH_FAILED) });
            } catch (err: any) {
                setModulesState((prev) => setModuleLaunchingState(prev, subjectId, false));
                addToast({ severity: 'error', body: extractErrorMessage(err, ERROR_MESSAGES.TRAINING_LAUNCH_FAILED) });
            }
        },
        [launchConfirmationModal, modulesState, addToast, launchNowCooldownMs, scheduleLaunchCooldownClear],
    );

    const handleCloseLaunchModal = useCallback(() => {
        setLaunchConfirmationModal(DEFAULT_MODAL_STATE);
    }, []);

    // ========================================================================
    // Effects
    // ========================================================================

    const createAmpSettingRef = useRef(createAmpSetting);
    const getAmpSettingValueRef = useRef(getAmpSettingValue);
    const tenantSettingsRef = useRef(tenantSettings);
    useEffect(() => {
        createAmpSettingRef.current = createAmpSetting;
    });
    useEffect(() => {
        getAmpSettingValueRef.current = getAmpSettingValue;
    });
    useEffect(() => {
        tenantSettingsRef.current = tenantSettings;
    });

    const settingsInitDoneRef = useRef(false);

    useEffect(() => {
        return () => {
            launchCooldownTimersRef.current.forEach((timer) => clearTimeout(timer));
            launchCooldownTimersRef.current.clear();
        };
    }, []);

    useEffect(() => {
        if (settingsLoading || !tenantSettings || Object.keys(tenantSettings).length === 0) {
            return;
        }
        if (settingsInitDoneRef.current) {
            return;
        }
        settingsInitDoneRef.current = true;

        const createSetting = createAmpSettingRef.current;
        const settings = tenantSettingsRef.current as any;

        try {
            try {
                const complianceEnabledKey = AMP_SETTINGS?.tenant?.COMPLIANCE_TRAINING_ENABLED?.id;
                if (complianceEnabledKey && !(complianceEnabledKey in settings)) {
                    createSetting(
                        complianceEnabledKey,
                        AMP_SETTINGS?.tenant?.COMPLIANCE_TRAINING_ENABLED?.defaultValue ?? false,
                    );
                }
            } catch (err) {
                /* ignore */
            }

            try {
                const activeSubjectsKey = AMP_SETTINGS?.tenant?.COMPLIANCE_TRAINING_ACTIVE_SUBJECTS?.id;
                if (activeSubjectsKey && !(activeSubjectsKey in settings)) {
                    createSetting(
                        activeSubjectsKey,
                        AMP_SETTINGS?.tenant?.COMPLIANCE_TRAINING_ACTIVE_SUBJECTS?.defaultValue ?? [],
                    );
                }
            } catch (err) {
                /* ignore */
            }
        } catch (err) {
            /* ignore */
        }
    }, [settingsLoading, tenantSettings]);

    useEffect(() => {
        if (i18nLoading || settingsLoading) {
            return;
        }

        const getVal = getAmpSettingValueRef.current;

        try {
            const rawSubjects = mergeComplianceTrainingModules(
                contentfulTrainingSubjects,
                parseComplianceTrainingCustomModulesFromSettingSignature(customModulesSettingSignature),
            );
            let activeSubjects: string[] = [];

            try {
                const activeSubjectsKey = AMP_SETTINGS?.tenant?.COMPLIANCE_TRAINING_ACTIVE_SUBJECTS?.id;
                if (activeSubjectsKey) {
                    const value = getVal(activeSubjectsKey);
                    activeSubjects = Array.isArray(value) ? value : [];
                }
            } catch (err) {
                /* ignore */
            }

            setModulesState((prev) =>
                buildComplianceTrainingModulesState(rawSubjects, activeSubjects, prev, DEFAULT_OVERDUE_DAYS),
            );
        } catch (err: any) {
            setModulesState([]);
        }
    }, [i18nLoading, settingsLoading, contentfulTrainingSubjects, customModulesSettingSignature]);

    const activeLaunchCooldownSignature = useMemo(
        () =>
            modulesState
                .filter(
                    (module) =>
                        typeof module.launchCooldownExpiresAt === 'number' &&
                        module.launchCooldownExpiresAt > Date.now(),
                )
                .map((module) => `${module.subject}:${module.launchCooldownExpiresAt}`)
                .join('|'),
        [modulesState],
    );

    useEffect(() => {
        if (i18nLoading || settingsLoading || !activeLaunchCooldownSignature) {
            return;
        }

        scheduleActiveLaunchCooldownsRef.current(modulesState);
    }, [i18nLoading, settingsLoading, activeLaunchCooldownSignature, modulesState]);

    // ========================================================================
    // Computed (Render helpers)
    // ========================================================================

    const launchModalModuleName = useMemo(() => {
        const modalModule = modulesState.find((m) => m.subject === launchConfirmationModal.subject);
        return launchConfirmationModal.name || modalModule?.name || '';
    }, [modulesState, launchConfirmationModal]);

    const launchModalIsLaunching = useMemo(
        () => modulesState.some((m) => m.subject === launchConfirmationModal.subject && m.isLaunching),
        [modulesState, launchConfirmationModal.subject],
    );

    if (i18nLoading || settingsLoading) {
        return (
            <div className="p-5 text-center">
                <Spinner animation="border" />
            </div>
        );
    }

    // ========================================================================
    // Render
    // ========================================================================

    return (
        <div className="d-flex flex-column engagement-flow__content compliance-training-page pt-4">
            {/* Navigation Tabs */}
            <div className="engagement-flow__tab-cont d-flex justify-content-between mb-4">
                <TrainingAndCoachingTabs />
            </div>

            {/* Main Content */}
            <div className="bg-text-black p-4 card compliance-training-content">
                <Typography variant="subtitle2" color="text-high-emphasis">
                    {t('COMPLIANCE_TRAINING_PAGE_SUBTITLE_1')}
                </Typography>
                <Typography variant="body3" color="text-high-emphasis" className="mt-2 mb-4">
                    {t('COMPLIANCE_TRAINING_PAGE_SUBTITLE_2')}
                </Typography>

                {/* Master Toggle */}
                <div className="d-flex flex-column gap-3 mb-4 p-3 master-toggle-container">
                    <ToggleTextPanel
                        header={t('ENABLE_COMPLIANCE_TRAINING_LABEL')}
                        checked={complianceTrainingEnabledValue}
                        onToggle={handleToggleComplianceTrainingEnabled}
                        disabled={savingComplianceTrainingEnabled}
                    />
                    {savingComplianceTrainingEnabled && (
                        <div className="d-flex align-items-center gap-2">
                            <Spinner animation="border" size="sm" />
                            <Typography variant="body4" color="text-medium-emphasis">
                                Saving...
                            </Typography>
                        </div>
                    )}
                </div>

                {/* Training Modules List */}
                {complianceTrainingEnabledValue && (
                    <div className="d-flex flex-column gap-3">
                        {modulesState.map((module) => {
                            // Calculate existing schedules to determine button label
                            const existingSchedules = allScheduleRecords.filter(
                                (r) => r.trainingConfig?.subject === module.subject,
                            );

                            return (
                                <TrainingModuleCard
                                    key={`module-${module.subject}`}
                                    module={module}
                                    isGlobalDisabled={!complianceTrainingEnabledValue}
                                    schedulesCount={existingSchedules.length}
                                    onToggleModule={handleToggleModule}
                                    onOverdueChange={handleOverdueChange}
                                    onConfigureSchedule={handleOpenSidePanel}
                                    onLaunchTraining={(subjectId, name) =>
                                        setLaunchConfirmationModal({ isOpen: true, subject: subjectId, name })
                                    }
                                />
                            );
                        })}
                    </div>
                )}
            </div>

            <LaunchTrainingModal
                show={launchConfirmationModal.isOpen}
                moduleName={launchModalModuleName}
                onClose={handleCloseLaunchModal}
                onLaunch={handleConfirmLaunchTraining}
                isLaunching={launchModalIsLaunching}
            />

            {activeModuleForSchedule && (
                <ScheduleSidePanel
                    isOpen={isSidePanelOpen}
                    onClose={() => setSidePanelOpen(false)}
                    moduleName={activeModuleForSchedule.name}
                    moduleSubject={activeModuleForSchedule.subject}
                    moduleData={activeModuleForSchedule.moduleData}
                    allScheduleRecords={allScheduleRecords}
                    isLoading={settingsLoading || isSavingSchedules}
                    onSave={handleSaveSidePanel}
                    syncIntervalSeconds={amplifierSyncIntervalSeconds}
                />
            )}
        </div>
    );
};
